import { t } from '@lingui/macro';
import { FixedBottomBarContainer, NoddiBasicCard } from 'noddi-ui-web';
import { useAuthContext } from 'noddi-web';

import { URLKeys, noddiAsync } from 'noddi-async';
import { AvailableBookingTimeWindow, BookingAddress, SelectedSalesItemsCar } from 'noddi-types';
import { groupCars } from 'noddi-util';
import { ApiErrorPageWithTrans } from '../../../components/ApiErrorPageWithTrans';
import { BookingScreen } from '../../../components/BookingFlow/BookingScreen';
import { BrandBanner } from '../../../components/BookingFlow/BrandAnnouncement';
import HelpScoutChat from '../../../components/BookingFlow/HelpScoutChat';
import { NewBookingErrorPage } from '../../../components/BookingFlow/NewBookingErrorPage';
import { CustomerSMSLogin } from '../../../components/CustomerSMSLogin';
import { NoddiLoadingScreenWithTrans } from '../../../components/NoddiLoadingScreenWithTrans';
import { CommonBookingInfoCard } from '../../../components/Summary/CommonBookingInfoCard';
import { ShoppingCartSummary } from '../../../components/Summary/ShoppingCartSummary';
import {
  useBookingActions,
  useBookingAddress,
  useBookingComment,
  useBookingSelectedSalesItemsCars,
  useBookingTimeWindow
} from '../bookingStore';
import CommentBox from './CommentBox';
import { ConfirmBooking } from './ConfirmBooking';

export const Summary = () => {
  const selectedSalesItemsCars = useBookingSelectedSalesItemsCars();
  const bookingTimeWindow = useBookingTimeWindow();
  const selectedAddress = useBookingAddress();

  if (!selectedAddress || !bookingTimeWindow || !selectedSalesItemsCars.length) {
    return <NewBookingErrorPage />;
  }
  return (
    <SummaryContent
      selectedSalesItemsCars={selectedSalesItemsCars}
      bookingTimeWindow={bookingTimeWindow}
      selectedAddress={selectedAddress}
    />
  );
};

const SummaryContent = ({
  selectedSalesItemsCars,
  bookingTimeWindow,
  selectedAddress
}: {
  selectedSalesItemsCars: SelectedSalesItemsCar[];
  bookingTimeWindow: AvailableBookingTimeWindow;
  selectedAddress: BookingAddress;
}) => {
  const { isLoggedIn } = useAuthContext();

  // TODO | New booking I guess we want to debounce this value, as now confirmBooking.tsx which subscribes is rerendered on every key stroke
  const { setComment } = useBookingActions();

  const { currentUserGroupId: userGroupId } = useAuthContext();
  const comment = useBookingComment();

  const {
    data: shoppingCart,
    isPending: isShoppingCartPending,
    error: shoppingCartError
  } = noddiAsync.usePostAsGetQuery({
    type: URLKeys.postGetShoppingCartForNewBooking,
    input: {
      addressId: selectedAddress?.id,
      cars: groupCars(selectedSalesItemsCars),
      deliveryWindow: bookingTimeWindow
        ? {
            id: bookingTimeWindow.deliveryWindowId,
            endsAt: bookingTimeWindow.endsAt,
            startsAt: bookingTimeWindow.startsAt
          }
        : undefined,
      userGroupId
    },
    queryConfig: {
      staleTime: Infinity
    }
  });

  const {
    data: bookingCategories,
    error: bookingCategoriesError,
    isPending: isBookingCategoriesPending
  } = noddiAsync.useGet({
    type: URLKeys.getBookingCategoriesForNewBooking,
    input: { addressId: selectedAddress?.id },
    queryConfig: { enabled: !!selectedAddress?.id }
  });

  if (shoppingCartError || bookingCategoriesError) {
    return <ApiErrorPageWithTrans error={[shoppingCartError, bookingCategoriesError]} />;
  }

  if (isShoppingCartPending || isBookingCategoriesPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  const bookingCategory = selectedSalesItemsCars[0]?.bookingCategorySlug;

  const brand = bookingCategories.find((category) => category.slug === bookingCategory)?.brand;

  return (
    <BookingScreen
      title={t`Does this look correct?`}
      customBottomBar={
        <FixedBottomBarContainer>
          <HelpScoutChat />
          <ConfirmBooking />
        </FixedBottomBarContainer>
      }
    >
      <div className='flex flex-col gap-4'>
        {isLoggedIn ? (
          <CommentBox comment={comment} handleChange={setComment} />
        ) : (
          <NoddiBasicCard>
            <CustomerSMSLogin />
          </NoddiBasicCard>
        )}

        <CommonBookingInfoCard address={selectedAddress} timeWindow={bookingTimeWindow} />

        <NoddiBasicCard>
          {bookingCategory && brand && <BrandBanner brand={brand} className='mb-3' />}
          <ShoppingCartSummary shoppingCart={shoppingCart.data} />
        </NoddiBasicCard>
      </div>
    </BookingScreen>
  );
};
